import { divideArrayIntoChunks } from '@pkg/basic'
import { count, inArray } from 'drizzle-orm'
import { z } from 'zod'
import { sqliteMaxHostParameterCount } from '../../constants'
import {
  insertAssistantProposalActionSchema,
  insertAssistantProposalSchema,
} from '../../querySchema'
import {
  assistantProposalActionTable,
  assistantProposalTable,
  assistantRejectedFingerprintTable,
} from '../../schema'
import type { DatabaseWithTransaction } from '../../types'
import {
  createAssistantActionKeyValue,
  parseAssistantChangePayloadJson,
  parseAssistantJsonObject,
} from './assistantWriteUtils'

/** SHA-256 hex文字列を検証するschema。 */
const fingerprintSchema = z.string().regex(/^[0-9a-f]{64}$/)

/** 保存前にproposal本体のDB共通制約を検証するschema。 */
const proposalRowWriteSchema = insertAssistantProposalSchema.extend({
  id: z.string().min(1),
  ruleKey: z.string().min(1),
  ruleVersion: z.number().int().positive(),
  rulePriority: z.number().int(),
  primarySourceId: z.string().min(1),
  primaryActionOrder: z.number().int().nonnegative(),
  changePayloadJson: z.string().min(1),
  explanationJson: z.string().min(1),
})

/** 保存前にproposal relationのDB共通制約を検証するschema。 */
const proposalActionRowWriteSchema = insertAssistantProposalActionSchema.extend(
  {
    proposalId: z.string().min(1),
    sourceId: z.string().min(1),
    actionOrder: z.number().int().nonnegative(),
    expectedHash: fingerprintSchema,
    displayOrder: z.number().int().nonnegative(),
  },
)

/** 解析1回分として保存する1提案のschema。 */
export const assistantProposalWriteSchema = z
  .object({
    proposal: proposalRowWriteSchema,
    actions: z.array(proposalActionRowWriteSchema).min(1),
  })
  .strict()

/** 解析結果保存APIの入力schema。 */
export const saveAssistantAnalysisResultInputSchema = z
  .object({
    proposals: z.array(assistantProposalWriteSchema),
    invalidRejectedFingerprints: z.array(fingerprintSchema),
  })
  .strict()

export type AssistantProposalWrite = z.infer<
  typeof assistantProposalWriteSchema
>

type SaveAssistantAnalysisResultParameters = {
  readonly database: DatabaseWithTransaction
  readonly proposals: readonly AssistantProposalWrite[]
  readonly invalidRejectedFingerprints: readonly string[]
}

/**
 * 1提案のrelationとchange payloadが同じtarget集合を表すことを検証する。
 * @param params 保存対象提案。
 * @returns なし。矛盾時は例外。
 */
const validateProposalSemantics = ({
  proposalWrite,
}: {
  readonly proposalWrite: AssistantProposalWrite
}): void => {
  const { proposal, actions } = proposalWrite
  const relationKeys = new Set<string>()
  const targetKeys = new Set<string>()
  for (const action of actions) {
    if (action.proposalId !== proposal.id) {
      throw new Error('proposal action references another proposal')
    }
    const actionKey = createAssistantActionKeyValue({
      sourceId: action.sourceId,
      actionOrder: action.actionOrder,
    })
    const relationKey = JSON.stringify([actionKey, action.role])
    if (relationKeys.has(relationKey)) {
      throw new Error('proposal action relation must be unique')
    }
    relationKeys.add(relationKey)
    if (action.role === 'target') {
      targetKeys.add(actionKey)
    }
  }
  if (targetKeys.size === 0) {
    throw new Error('assistant proposal must contain a target')
  }

  const primaryKey = createAssistantActionKeyValue({
    sourceId: proposal.primarySourceId,
    actionOrder: proposal.primaryActionOrder,
  })
  if (!targetKeys.has(primaryKey)) {
    throw new Error('assistant proposal primary action must be a target')
  }

  const payload = parseAssistantChangePayloadJson({
    changePayloadJson: proposal.changePayloadJson,
  })
  const payloadKeys = new Set(
    payload.actions.map((action) =>
      createAssistantActionKeyValue({
        sourceId: action.sourceId,
        actionOrder: action.actionOrder,
      }),
    ),
  )
  if (
    payloadKeys.size !== targetKeys.size ||
    [...payloadKeys].some((key) => !targetKeys.has(key))
  ) {
    throw new Error('assistant change payload must match proposal targets')
  }
  parseAssistantJsonObject({
    json: proposal.explanationJson,
    label: 'assistant explanation',
  })
}

/**
 * 全rule評価済みの提案群と明確に無効な拒否fingerprint cleanupを原子的に保存する。
 * @param params DB、全提案、invalidと確定したfingerprint。
 * @returns 保存した提案件数。
 */
export const saveAssistantAnalysisResult = async ({
  database: { transaction },
  proposals,
  invalidRejectedFingerprints,
}: SaveAssistantAnalysisResultParameters): Promise<number> => {
  const parsed = saveAssistantAnalysisResultInputSchema.parse({
    proposals,
    invalidRejectedFingerprints,
  })
  const proposalIds = new Set<string>()
  for (const proposalWrite of parsed.proposals) {
    if (proposalIds.has(proposalWrite.proposal.id)) {
      throw new Error('assistant proposal id must be unique')
    }
    proposalIds.add(proposalWrite.proposal.id)
    validateProposalSemantics({ proposalWrite })
  }

  const proposalRows = parsed.proposals.map((item) => item.proposal)
  const actionRows = parsed.proposals.flatMap((item) => item.actions)

  await transaction([
    (tx) => [
      (async () => {
        const rows = await tx
          .select({ count: count() })
          .from(assistantProposalTable)
        if ((rows[0]?.count ?? 0) !== 0) {
          throw new Error('assistant proposals already exist')
        }
      })(),
    ],
    (tx) =>
      divideArrayIntoChunks(
        proposalRows,
        Math.max(1, Math.floor(sqliteMaxHostParameterCount / 10)),
      ).map((chunk) => tx.insert(assistantProposalTable).values([...chunk])),
    (tx) =>
      divideArrayIntoChunks(
        actionRows,
        Math.max(1, Math.floor(sqliteMaxHostParameterCount / 6)),
      ).map((chunk) =>
        tx.insert(assistantProposalActionTable).values([...chunk]),
      ),
    (tx) =>
      divideArrayIntoChunks(
        parsed.invalidRejectedFingerprints,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .delete(assistantRejectedFingerprintTable)
          .where(
            inArray(assistantRejectedFingerprintTable.fingerprint, [...chunk]),
          ),
      ),
  ])

  return proposalRows.length
}
